import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCheckInToken } from "../../services/api/checkin.service";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";

const PROD_API_URL = "https://degencoinflip.getid.ee";
const DEV_API_URL = "https://degencoinflip.sb.getid.dev";

declare global {
  interface Window {
    getidWebSdk: {
      init: (config: any) => void;
    };
  }
}

interface KycVerificationProps {
  startPolling: () => Promise<void>;
  pollingLoading: boolean;
  pollingError: string | null;
}

export function KycVerification({
  startPolling,
  pollingLoading,
  pollingError,
}: KycVerificationProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkSdkAvailability = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 5 seconds total
      let attempts = 0;

      const check = () => {
        if (window.getidWebSdk) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error("GetID SDK failed to load"));
        } else {
          attempts++;
          setTimeout(check, 100);
        }
      };

      check();
    });
  };

  const initializeCheckInWidget = async () => {
    setIsLoading(true);
    setError(null);
    setIsModalOpen(true);
    setShowSpinner(true);

    console.log("Initializing CheckIn widget...");
    try {
      // Wait for SDK to be available
      await checkSdkAvailability();

      const { token, externalId } = await getCheckInToken();
      // Keep showing spinner for 4 seconds after API call completes
      setTimeout(() => {
        setShowSpinner(false);
        // Remove loading state after fade animation completes
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }, 10000);

      window.getidWebSdk.init({
        apiUrl: process.env.REACT_APP_HIVEMIND_API_URL?.includes("dev")
          ? DEV_API_URL
          : PROD_API_URL,
        jwt: token,
        containerId: "targetContainer",
        flowName: "ID-Selfie-Liveness",
        metadata: {
          externalId,
        },
        onComplete: async (data: any) => {
          console.log("Verification completed:", data);
          const { applicationId } = data;
          console.log("Application ID:", applicationId);
          setIsModalOpen(false);
          // Start polling for dashboard data
          await startPolling();

          // Navigate only if we're not already on dashboard and not in polling state
          if (!pollingLoading && !pollingError && location.pathname !== "/") {
            navigate("/");
          }
        },
        onFail: ({ message }: any) => {
          console.error("Verification failed:", message);
          setError(`Verification failed: ${message}`);
          setIsModalOpen(false);
        },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to initialize verification. Please try again.";
      setError(errorMessage);
      setIsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async () => {
    await initializeCheckInWidget();
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 font-body">
            {error}
          </div>
        )}
        {pollingError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 font-body">
            {pollingError}
          </div>
        )}
        {pollingLoading && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-4 animate-pulse">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-500"></div>
                <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent rounded-full animate-ping border-t-blue-400 animation-delay-500"></div>
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-blue-700 font-medium font-heading">
                  Verifying Your Identity
                </h3>
                <div className="flex items-center justify-center space-x-1">
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  ></span>
                </div>
                <p className="text-blue-600 text-sm font-body">
                  This usually takes less than a minute
                </p>
              </div>
            </div>
          </div>
        )}
        <Button
          variant="degen"
          className="w-full group px-8 py-6 transition-all duration-300 text-base font-normal tracking-wide rounded-md font-heading"
          type="submit"
          onClick={() => handleFormSubmit()}
          disabled={isLoading || pollingLoading}
          data-kyc-verify-button
        >
          <span className="inline-flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300">
            VERIFY MY ACCOUNT
            {isLoading || pollingLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 opacity-100 transition-all duration-300" />
            )}
          </span>
        </Button>
        <p className="text-sm text-gray-500 mt-4 font-body">
          Your data is secure and used only for this one time verification.
        </p>
      </div>

      <div
        className={`fixed inset-0 bg-black transition-all duration-300 ease-in-out
        ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }
        z-50 flex items-start sm:items-center justify-center p-0 sm:p-4`}
        onClick={handleBackgroundClick}
      >
        <div
          className={`bg-white w-full h-[100%] sm:rounded-lg sm:w-full sm:max-w-3xl 
          sm:h-[300px] md:h-[600px] sm:mx-4 overflow-y-auto
          transition-all duration-300 transform
          ${isModalOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"}`}
        >
          {showSpinner && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-zinc-200 border-t-zinc-800 rounded-full animate-spin" />
            </div>
          )}
          <div id="targetContainer" className="w-full h-full" />
        </div>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium mb-4">Cancel Verification?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel the verification process?
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
              >
                No, Continue
              </Button>
              <Button variant="destructive" onClick={handleConfirmCancel}>
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
